import type { CSSProperties } from 'react';

import { cn } from '@ps/ui/utils';
import { Heart } from 'lucide-react';

/**
 * Emitting-hearts icon for the tipping button — a static filled heart with small
 * hearts continuously rising, drifting, and fading above it. A pure-CSS port of
 * the legacy `AnimatedHeartIcon` (RN moti): 5 staggered particles + 1 main heart,
 * driven by the `tip-heart-rise` keyframe. No JS animation loop (visitor-bio JS
 * stays minimal); hidden under `prefers-reduced-motion`.
 *
 * Particles rise ~56px above the icon, so the host must NOT clip them
 * (`overflow: visible` up to the pill — clip the goal progress fill in its own
 * layer, not on the pill).
 */

/** Per-particle horizontal drift (px) + stagger (ms) — matches the legacy delays. */
const PARTICLES: ReadonlyArray<{ driftX: number; delayMs: number }> = [
  { driftX: 0, delayMs: 0 },
  { driftX: -16, delayMs: 350 },
  { driftX: 16, delayMs: 700 },
  { driftX: -10, delayMs: 500 },
  { driftX: 10, delayMs: 200 },
];

interface Props {
  /** Main heart size in px; particles are half. */
  size?: number;
  className?: string;
}

export function AnimatedHeartIcon({ size = 20, className }: Props) {
  const particleSize = Math.round(size * 0.5);

  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-flex items-center justify-center',
        className,
      )}
      style={{ width: size, height: size }}
    >
      {PARTICLES.map(({ driftX, delayMs }, index) => (
        <Heart
          key={index}
          className="animate-tip-heart-rise absolute fill-red-500 stroke-red-500 motion-reduce:hidden"
          style={
            {
              width: particleSize,
              height: particleSize,
              '--tip-drift-x': `${driftX}px`,
              animationDelay: `${delayMs}ms`,
            } as CSSProperties
          }
        />
      ))}
      <Heart
        className="relative fill-red-500 stroke-red-500"
        style={{ width: size, height: size }}
      />
    </span>
  );
}
